import { Link } from "react-router-dom";
import {
  LayoutDashboard, FileUp, Search, ShieldCheck, Layers, Wand2, FilePlus2,
  Mail, MessageSquare, Users, ClipboardList, CreditCard, Settings as SettingsIcon,
  GraduationCap, Rocket, ShieldAlert, ArrowRight,
} from "lucide-react";

const STEPS = [
  { n: 1, t: "Upload your resume", d: "Start by adding your existing CV in PDF or DOCX." },
  { n: 2, t: "Run ATS Resume Review", d: "See how friendly your resume is to applicant tracking systems." },
  { n: 3, t: "Generate an improved resume", d: "Cavilow rewrites it for clarity, keywords and impact — using only your real content." },
  { n: 4, t: "Find matching jobs", d: "Search openings tailored to your title, country and experience." },
  { n: 5, t: "Tailor your resume and prepare", d: "Customise it per job, then run interview practice." },
];

const TABS = [
  {
    to: "/dashboard",
    icon: LayoutDashboard,
    title: "Dashboard",
    body: "This is your main home screen after login. Here you can see your resume status, recent activity, saved jobs, ATS score, and quick shortcuts to the main tools.",
    accent: "#1E6FE6", bg: "#E6F0FF",
  },
  {
    to: "/upload",
    icon: FileUp,
    title: "Upload Resume",
    body: "Upload your existing CV or resume in PDF or DOCX format. Cavilow reads your resume and uses it to help with job matching, ATS review, resume improvement, cover letters, and interview preparation.",
    accent: "#0EA5E9", bg: "#E0F2FE",
  },
  {
    to: "/jobs",
    icon: Search,
    title: "Find Jobs",
    body: "Search for jobs based on your resume, selected country, city, job title, industry, and experience level. Cavilow shows matching jobs and redirects you to the original job platform when you want to apply.",
    accent: "#10B981", bg: "#D1FAE5",
  },
  {
    to: "/ats",
    icon: ShieldCheck,
    title: "ATS Resume Review",
    body: "Check how ATS-friendly your resume is. Cavilow reviews your resume structure, keywords, formatting, skills, and impact. You can also generate an improved resume version based on the review.",
    accent: "#22C55E", bg: "#DCFCE7",
  },
  {
    to: "/versions",
    icon: Layers,
    title: "Resume Versions",
    body: "Create different versions of your resume, such as ATS-friendly, modern professional, senior-level, industry-specific, or one-page resume. You can preview, save, and download the version you prefer.",
    accent: "#F59E0B", bg: "#FEF3C7",
  },
  {
    to: "/tailor",
    icon: Wand2,
    title: "Tailor Resume",
    body: "Paste a job description and Cavilow will adjust your resume for that specific role. It improves wording, keywords, and structure based only on your real experience.",
    accent: "#8B5CF6", bg: "#EDE9FE",
  },
  {
    to: "/create-cv",
    icon: FilePlus2,
    title: "Create New CV",
    body: "Use this section if you want to create a new CV from scratch. Enter your work experience, education, skills, achievements, and career details, and Cavilow will generate a professional CV.",
    accent: "#EC4899", bg: "#FCE7F3",
  },
  {
    to: "/cover-letter",
    icon: Mail,
    title: "Cover Letter",
    body: "Generate a cover letter for a specific job, company, or role. You can choose the tone and use it along with your resume when applying.",
    accent: "#FB7185", bg: "#FFE4E6",
  },
  {
    to: "/interview",
    icon: MessageSquare,
    title: "Interview Practice",
    body: "Prepare for interviews by generating practice questions based on the job title, company, job description, country, and experience level. Cavilow also gives answer guidance and tips.",
    accent: "#06B6D4", bg: "#CFFAFE",
  },
  {
    to: null,
    icon: Users,
    title: "Networking",
    body: "Get suggested messages to connect with recruiters, HR professionals, or employees working at the company you are interested in. This helps you approach people professionally.",
    accent: "#6366F1", bg: "#E0E7FF",
    soon: true,
  },
  {
    to: "/tracker",
    icon: ClipboardList,
    title: "Application Tracker",
    body: "Save jobs and track your application progress. You can mark jobs as saved, applied, interview scheduled, offer received, rejected, or follow-up required.",
    accent: "#0F172A", bg: "#F1F5F9",
  },
  {
    to: "/pricing",
    icon: CreditCard,
    title: "Pricing",
    body: "View the available free and premium plans and understand which features are included in each plan.",
    accent: "#14B8A6", bg: "#CCFBF1",
  },
  {
    to: "/settings",
    icon: SettingsIcon,
    title: "Settings",
    body: "Manage your profile, account details, saved resumes, subscription status, and account preferences.",
    accent: "#64748B", bg: "#F1F5F9",
  },
];

export default function Tutorial() {
  return (
    <div className="space-y-12" data-testid="tutorial-root">
      {/* Hero */}
      <div className="fade-up">
        <div className="chip chip-amber mb-3" data-testid="tutorial-eyebrow">
          <GraduationCap size={12} /> Tutorial
        </div>
        <h1
          className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight"
          data-testid="tutorial-title"
        >
          Tutorial
        </h1>
        <p className="mt-3 text-slate-400 text-lg max-w-2xl" data-testid="tutorial-subtitle">
          Learn how to use Cavilow step by step.
        </p>
      </div>

      {/* 5 Steps */}
      <section className="fade-up" data-testid="tutorial-steps">
        <div className="flex items-center gap-2 mb-5">
          <Rocket size={18} className="text-[#80FFEA]" />
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
            How to use Cavilow in 5 steps
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map((s) => (
            <div
              key={s.n}
              data-testid={`tutorial-step-${s.n}`}
              className="relative glass p-5 hover:border-[var(--cavilo-blue)] hover:shadow-[0_18px_40px_-25px_rgba(30,111,230,0.6)] transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="grid place-items-center w-10 h-10 rounded-2xl bg-[var(--cavilo-blue)] text-white font-display font-black text-lg">
                  {s.n}
                </span>
                {s.n < 5 && (
                  <ArrowRight size={16} className="text-slate-300 hidden lg:block" />
                )}
              </div>
              <div className="font-display font-bold text-base text-white leading-snug">
                {s.t}
              </div>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tab guide */}
      <section className="fade-up" data-testid="tutorial-tabs">
        <div className="flex items-center gap-2 mb-5">
          <LayoutDashboard size={18} className="text-[#80FFEA]" />
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
            What each tab does
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const CardInner = (
              <>
                <div className="flex items-start gap-4">
                  <span
                    className="grid place-items-center w-12 h-12 rounded-2xl shrink-0"
                    style={{ background: tab.bg, color: tab.accent }}
                  >
                    <Icon size={22} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-lg text-white">
                        {tab.title}
                      </h3>
                      {tab.soon && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100">
                          Coming soon
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{tab.body}</p>
                    {tab.to && (
                      <span
                        className="mt-3 inline-flex items-center gap-1 text-xs font-bold"
                        style={{ color: tab.accent }}
                      >
                        Open {tab.title} <ArrowRight size={12} />
                      </span>
                    )}
                  </div>
                </div>
              </>
            );

            const baseClass =
              "block glass p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-25px_rgba(15,23,42,0.45)] hover:border-white/15";

            return tab.to ? (
              <Link
                key={tab.title}
                to={tab.to}
                data-testid={`tutorial-card-${tab.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={baseClass}
              >
                {CardInner}
              </Link>
            ) : (
              <div
                key={tab.title}
                data-testid={`tutorial-card-${tab.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`${baseClass} cursor-default`}
              >
                {CardInner}
              </div>
            );
          })}
        </div>
      </section>

      {/* Honesty note */}
      <section className="fade-up" data-testid="tutorial-honesty">
        <div className="rounded-3xl border border-amber-200 bg-amber-50/70 p-6 sm:p-7 flex items-start gap-4">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white text-amber-600 shrink-0 border border-amber-100">
            <ShieldAlert size={22} />
          </span>
          <div>
            <h3 className="font-display font-bold text-lg text-white">
              A note on honesty
            </h3>
            <p className="mt-1.5 text-sm text-slate-200 leading-relaxed">
              Cavilow helps improve your resume, but it does not add fake experience, fake
              companies, fake education, fake certifications, or false achievements.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="fade-up pb-4">
        <div className="rounded-3xl bg-[var(--cavilo-ink)] text-white p-7 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <h3 className="font-display font-bold text-2xl tracking-tight">Ready to start?</h3>
            <p className="mt-1.5 text-sm text-slate-300 max-w-md">
              Upload your resume and Cavilow will guide you through the rest.
            </p>
          </div>
          <Link
            to="/upload"
            data-testid="tutorial-upload-cta"
            className="inline-flex items-center gap-2 rounded-2xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-5 py-3 text-sm font-bold btn-press"
          >
            <FileUp size={16} /> Upload resume
          </Link>
        </div>
      </section>
    </div>
  );
}
